/**
 * 経費管理画面
 * みんなの青色申告連携対応
 * Created: 2025-10-08
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  message,
  Space,
  Tag
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import apiClient from '../../utils/apiClient';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { TextArea } = Input;

interface Expense {
  id: number;
  expense_date: string;
  account_code: string;
  account_name: string;
  amount: number;
  tax_amount: number;
  tax_code: string;
  payment_method: string;
  vendor: string;
  description: string;
  memo: string;
  is_deductible: number;
  created_at: string;
}

interface ExpenseCategory {
  id: number;
  category_name: string;
  default_account_code: string;
  default_tax_code: string;
  sort_order: number;
}

const ExpenseManagement: React.FC = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [form] = Form.useForm();

  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
  }, [filterYear, filterMonth]);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/accounting-export/expense-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('カテゴリー取得エラー:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/accounting-export/expenses', {
        params: {
          year: filterYear,
          month: filterMonth
        }
      });
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('経費取得エラー:', error);
      message.error('経費データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingExpense(null);
    form.resetFields();
    form.setFieldsValue({
      expense_date: dayjs(),
      payment_method: 'cash',
      tax_code: '20',
      is_deductible: 1
    });
    setModalVisible(true);
  };

  const handleEdit = (record: Expense) => {
    setEditingExpense(record);
    form.setFieldsValue({
      ...record,
      expense_date: dayjs(record.expense_date)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '経費を削除しますか？',
      content: 'この操作は取り消せません。',
      onOk: async () => {
        try {
          await apiClient.delete(`/accounting-export/expenses/${id}`);
          message.success('経費を削除しました');
          fetchExpenses();
        } catch (error) {
          message.error('削除に失敗しました');
        }
      }
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        amount: parseInt(values.amount),
        tax_amount: parseInt(values.tax_amount || 0)
      };

      if (editingExpense) {
        await apiClient.put(`/accounting-export/expenses/${editingExpense.id}`, data);
        message.success('経費を更新しました');
      } else {
        await apiClient.post('/accounting-export/expenses', data);
        message.success('経費を登録しました');
      }

      setModalVisible(false);
      fetchExpenses();
    } catch (error) {
      message.error(editingExpense ? '更新に失敗しました' : '登録に失敗しました');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/accounting-export/expenses-aoiro', {
        params: {
          year: filterYear,
          month: filterMonth
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv; charset=Shift_JIS' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `経費仕訳_${filterYear}年${filterMonth}月.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      message.success('CSVファイルをダウンロードしました');
    } catch (error) {
      message.error('CSV出力に失敗しました');
    }
  };

  const columns = [
    {
      title: '日付',
      dataIndex: 'expense_date',
      key: 'expense_date',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY/MM/DD')
    },
    {
      title: '勘定科目',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 150
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (amount: number) => `¥${amount.toLocaleString()}`
    },
    {
      title: '支払先',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 150
    },
    {
      title: '摘要',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '支払方法',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 100,
      render: (method: string) => {
        const labels: { [key: string]: { text: string; color: string } } = {
          cash: { text: '現金', color: 'green' },
          bank: { text: '銀行', color: 'blue' },
          card: { text: 'カード', color: 'purple' }
        };
        const label = labels[method] || { text: method, color: 'default' };
        return <Tag color={label.color}>{label.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: Expense) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            削除
          </Button>
        </Space>
      )
    }
  ];

  const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalTax = expenses.reduce((sum, exp) => sum + exp.tax_amount, 0);

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <span>経費管理</span>
            <Select
              value={filterYear}
              onChange={setFilterYear}
              style={{ width: 100 }}
            >
              {[2024, 2025, 2026].map(year => (
                <Option key={year} value={year}>{year}年</Option>
              ))}
            </Select>
            <Select
              value={filterMonth}
              onChange={setFilterMonth}
              style={{ width: 80 }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <Option key={month} value={month}>{month}月</Option>
              ))}
            </Select>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              経費登録
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={expenses.length === 0}
            >
              CSV出力（旧/非推奨）
            </Button>
          </Space>
        }
      >
        <div
          style={{
            marginBottom: 16,
            padding: 16,
            border: '2px solid #91caff',
            background: '#e6f4ff',
            borderRadius: 8
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#003a8c', marginBottom: 10 }}>
            会計ソフト（青色申告）に取り込む経費仕訳は /accounting の「経費仕訳（推奨）」から出力してください。
          </div>
          <Button
            type="primary"
            onClick={() => navigate('/accounting')}
          >
            /accounting の経費仕訳（推奨）へ
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={expenses}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          summary={() => (
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>合計</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong>¥{totalAmount.toLocaleString()}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} colSpan={4}>
                <span style={{ marginLeft: 16 }}>
                  消費税: ¥{totalTax.toLocaleString()}
                </span>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Modal
        title={editingExpense ? '経費編集' : '経費登録'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        okText="保存"
        cancelText="キャンセル"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="expense_date"
            label="日付"
            rules={[{ required: true, message: '日付を選択してください' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="account_code"
            label="勘定科目"
            rules={[{ required: true, message: '勘定科目を選択してください' }]}
          >
            <Select
              placeholder="勘定科目を選択"
              onChange={(value) => {
                const cat = categories.find(c => c.default_account_code === value);
                if (cat) {
                  form.setFieldsValue({ account_name: cat.category_name });
                }
              }}
            >
              {categories.map(cat => (
                <Option key={cat.id} value={cat.default_account_code}>
                  {cat.category_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="account_name"
            label="勘定科目名"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="amount"
            label="金額（税抜）"
            rules={[{ required: true, message: '金額を入力してください' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="tax_amount"
            label="消費税額"
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label="支払方法"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="cash">現金</Option>
              <Option value="bank">銀行振込</Option>
              <Option value="card">クレジットカード</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="vendor"
            label="支払先"
          >
            <Input placeholder="支払先名" />
          </Form.Item>

          <Form.Item
            name="description"
            label="摘要"
            rules={[{ required: true, message: '摘要を入力してください' }]}
          >
            <Input placeholder="例：食材仕入（野菜・豚肉）" />
          </Form.Item>

          <Form.Item
            name="memo"
            label="メモ"
          >
            <TextArea rows={2} placeholder="備考・メモ" />
          </Form.Item>

          <Form.Item name="tax_code" hidden initialValue="20">
            <Input />
          </Form.Item>

          <Form.Item name="is_deductible" hidden initialValue={1}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ExpenseManagement;
